// Display formatting shared across every G1 Health section. Pure and
// venue-agnostic — no thresholds, no tone decisions, nothing that knows what a
// healthy number looks like. Those live with the section that judges them.

/** Splits an account key ("org:<uuid>" | "user:<uuid>") into its scope and a
 *  short id, eliding the middle of anything longer than 10 chars so a UUID fits
 *  a table cell without a tooltip. */
export function shortKey(key: string): { scope: string; id: string } {
  const [scope, id = ''] = key.split(':');
  return { scope, id: id.length > 10 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id };
}

/** The org id inside an account key, or null for a personal ("user:") account.
 *  Returning null rather than throwing is what lets the org drill-down filter
 *  personal accounts out by the same predicate that selects org ones. */
export function orgIdOf(key: string): string | null {
  return key.startsWith('org:') ? key.slice(4) : null;
}

/** The org drill-down predicate: does this org id fall inside the current
 *  selection? `'all'` admits everything including personal (`user:`) accounts,
 *  which have no org id at all; selecting a specific org therefore excludes them,
 *  and that is deliberate rather than an oversight about nulls.
 *
 *  Shared because the page and the reject panel each filter their own list by it,
 *  and a drill-down that meant two different things in two places would be a
 *  quiet way to show one org's numbers under another org's name. */
export const orgMatcher = (selectedOrg: string) => (orgId: string | null) =>
  selectedOrg === 'all' || orgId === selectedOrg;

/** Compact age: seconds under a minute, then `m` + zero-padded seconds. */
export function fmtAge(ms: number): string {
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m${String(s % 60).padStart(2, '0')}`;
}
